import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Send, UserCheck, History, DollarSign, ShieldAlert, Image, FileText, Download, Pencil, Flag, ListChecks } from "lucide-react";
import { api, errMsg, fmtDT, usd, downloadBlob } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Btn, Field, Input, Textarea, Select, Section, Row, Money } from "../../components/kit";
import { JobSummary, CustomerCard, NextStep, PhotoJumpButton } from "../../components/JobParts";
import PricingBox from "../../components/PricingBox";
import Condition from "../../components/Condition";
import { PhotoSlot, PhotoViewer } from "../../components/Photos";

function useAct(job, reload) {
  const [busy, setBusy] = useState("");
  const act = async (key, path, body, msg) => {
    setBusy(key);
    try {
      await api.post(`/admin/jobs/${job.id}/${path}`, body);
      toast.success(msg || "Done");
      reload();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    } finally {
      setBusy("");
    }
  };
  return [busy, act];
}

function Dispatch({ job, reload }) {
  const [busy, act] = useAct(job, reload);
  const [specs, setSpecs] = useState([]);
  const [pub, setPub] = useState({ approval_method: "text", approval_note: "" });
  const [asg, setAsg] = useState({ specialist_id: "", reason: "", force: false });
  const [reason, setReason] = useState("");
  useEffect(() => {
    api.get("/admin/specialists").then(({ data }) => setSpecs(data.filter((s) => s.status === "approved"))).catch(() => {});
  }, []);
  const closed = ["completed", "cancelled"].includes(job.status);
  return (
    <Section title="Offers & assignment" icon={Send} testid="dispatch-section">
      {job.status === "draft" && (
        <div className="space-y-2">
          <p className="text-sm text-neutral-400">Publish only after the customer approves the scope and price.</p>
          <div className="grid grid-cols-2 gap-2">
            <Select value={pub.approval_method} onChange={(e) => setPub({ ...pub, approval_method: e.target.value })} data-testid="publish-approval-method">
              <option value="text">Approved by text</option><option value="phone">Approved by phone</option><option value="in_person">Approved in person</option><option value="email">Approved by email</option>
            </Select>
            <Input placeholder="Note (date, message)" value={pub.approval_note} onChange={(e) => setPub({ ...pub, approval_note: e.target.value })} data-testid="publish-approval-note" />
          </div>
          <Btn className="w-full" onClick={() => act("pub", "publish", pub, "Published — offers going out")} loading={busy === "pub"} data-testid="publish-job-btn">Customer approved · Publish</Btn>
        </div>
      )}
      {job.status === "open" && (
        <div className="text-sm text-neutral-400" data-testid="dispatch-state">
          {job.dispatch?.needs_dispatch ? <span className="text-red-400">Needs dispatch — no one accepted. Assign directly or send offers again.</span> : `Round ${job.dispatch?.round || 0} offers out. Expands automatically on the server.`}
        </div>
      )}
      {job.offers?.length > 0 && (
        <div className="max-h-56 overflow-y-auto rounded-xl border border-[#262626]" data-testid="offers-list">
          {job.offers.map((o) => (
            <div key={o.id} className="flex justify-between text-xs px-3 py-2 border-b border-[#1f1f1f] last:border-0">
              <span>{o.specialist_name} · ~{o.distance_miles} mi · R{o.round}{o.cycle > 1 ? ` · C${o.cycle}` : ""}</span>
              <span className={`font-mono uppercase ${o.status === "accepted" ? "text-emerald-400" : o.status === "pending" ? "text-[#FF6B00]" : "text-neutral-500"}`}>{o.status}</span>
            </div>
          ))}
        </div>
      )}
      {!closed && job.status !== "draft" && (
        <div className="space-y-2 pt-2 border-t border-[#1f1f1f]">
          <div className="text-xs uppercase tracking-wider text-neutral-500">{job.assigned_to ? "Reassign (revokes current access)" : "Assign directly"}</div>
          <Select value={asg.specialist_id} onChange={(e) => setAsg({ ...asg, specialist_id: e.target.value })} data-testid="assign-specialist-select">
            <option value="">Choose specialist…</option>
            {specs.filter((s) => s.id !== job.assigned_to).map((s) => <option key={s.id} value={s.id}>{s.name}{s.available ? " · Available" : ""}</option>)}
          </Select>
          <Input placeholder="Reason (optional)" value={asg.reason} onChange={(e) => setAsg({ ...asg, reason: e.target.value })} data-testid="assign-reason" />
          <label className="flex gap-2 text-xs text-neutral-400"><input type="checkbox" checked={asg.force} onChange={(e) => setAsg({ ...asg, force: e.target.checked })} className="accent-[#FF6B00]" data-testid="assign-force" />Override schedule conflict</label>
          <Btn variant="outline" className="w-full" disabled={!asg.specialist_id} loading={busy === "asg"} onClick={() => act("asg", "assign", asg, "Assigned")} data-testid="assign-btn"><UserCheck className="w-4 h-4" />{job.assigned_to ? "Reassign" : "Assign"}</Btn>
        </div>
      )}
      {job.status !== "draft" && job.status !== "completed" && (
        <div className="space-y-2 pt-2 border-t border-[#1f1f1f]">
          <Input placeholder="Reason (required for cancel)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dispatch-reason" />
          <div className="grid grid-cols-2 gap-2">
            <Btn variant="outline" size="sm" loading={busy === "re"} onClick={() => act("re", "reopen", { reason }, "Reopened — offers going out")} data-testid="reopen-btn">{job.status === "open" ? "Send offers again" : "Reopen"}</Btn>
            {!closed && <Btn variant="danger" size="sm" loading={busy === "can"} onClick={() => act("can", "cancel", { reason }, "Cancelled")} data-testid="cancel-job-btn">Cancel job</Btn>}
          </div>
        </div>
      )}
    </Section>
  );
}

function Quotes({ job, reload }) {
  const [busy, act] = useAct(job, reload);
  const [open, setOpen] = useState(false);
  const [r, setR] = useState({ ...job.pricing, description: job.description, reason: "" });
  const canRevise = !["draft", "completed", "cancelled"].includes(job.status);
  const num = (v) => Number(v || 0);
  const submit = async () => {
    const ok = await act("rev", "revise", {
      reason: r.reason, description: r.description,
      pricing: { subtotal: num(r.subtotal), parts_cost: num(r.parts_cost), payout: num(r.payout), other_costs: num(r.other_costs), tax_rate: num(r.tax_rate), tax_confirmed: true },
    }, "Quote revised — customer approval required");
    if (ok) setOpen(false);
  };
  return (
    <Section title="Pricing & quotes" icon={DollarSign} testid="quotes-section"
      right={canRevise && <Btn variant="outline" size="sm" onClick={() => setOpen(!open)} data-testid="revise-quote-toggle">Revise quote</Btn>}>
      <PricingBox p={job.pricing} />
      <div className="text-xs text-neutral-500">Accepted at v{job.accepted_quote_version || "—"} · payout at acceptance {job.accepted_payout != null ? usd(job.accepted_payout) : "—"} · current payout {usd(job.pricing.payout)} (revisions shown below)</div>
      {open && (
        <div className="space-y-2 rounded-xl border border-[#333] p-3" data-testid="revise-quote-form">
          <div className="grid grid-cols-2 gap-2">
            {[["subtotal", "Subtotal"], ["parts_cost", "Parts"], ["payout", "Payout"], ["other_costs", "Other"], ["tax_rate", "Tax %"]].map(([k, l]) => (
              <Field key={k} label={l}><Input type="number" step="0.01" value={r[k]} onChange={(e) => setR({ ...r, [k]: e.target.value })} data-testid={`revise-${k}`} /></Field>
            ))}
          </div>
          <Textarea value={r.description} onChange={(e) => setR({ ...r, description: e.target.value })} placeholder="Revised scope" data-testid="revise-description" />
          <Input value={r.reason} onChange={(e) => setR({ ...r, reason: e.target.value })} placeholder="Reason (e.g. extra work found)" data-testid="revise-reason" />
          <PricingBox p={r} />
          <Btn className="w-full" onClick={submit} loading={busy === "rev"} data-testid="revise-submit-btn">Save revision v{job.current_quote_version + 1}</Btn>
        </div>
      )}
      <div className="space-y-1">
        {job.quotes.map((q) => (
          <div key={q.version} className="text-xs border-l-2 border-[#333] pl-3 py-1" data-testid="quote-version">
            <div className="text-neutral-300">v{q.version} · {usd(q.pricing.subtotal)} · payout {usd(q.pricing.payout)} · {q.reason}</div>
            <div className="text-neutral-500">{q.by} · {fmtDT(q.at)}{q.customer_preapproval && ` · customer approved via ${q.customer_preapproval.method}`}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Review({ job, reload }) {
  const [busy, act] = useAct(job, reload);
  const [reason, setReason] = useState("");
  const [ov, setOv] = useState({ phase: "before", reason: "" });
  return (
    <Section title="Review & exceptions" icon={ShieldAlert} testid="review-section">
      {job.status === "awaiting_review" && (
        <div className="space-y-2">
          <Input placeholder="Review note / reason to return" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="review-note" />
          <div className="grid grid-cols-2 gap-2">
            <Btn variant="success" onClick={() => act("ok", "complete", { reason }, "Completed")} loading={busy === "ok"} data-testid="review-complete-btn">Approve · Complete</Btn>
            <Btn variant="outline" onClick={() => act("ret", "return", { reason }, "Returned")} loading={busy === "ret"} data-testid="review-return-btn">Return to specialist</Btn>
          </div>
        </div>
      )}
      <div className="text-xs text-neutral-500">Documentation exception (inaccessible items or technical issues). Reason is recorded.</div>
      <div className="grid grid-cols-3 gap-2">
        <Select value={ov.phase} onChange={(e) => setOv({ ...ov, phase: e.target.value })} data-testid="override-phase"><option value="before">Before</option><option value="after">After</option></Select>
        <Input className="col-span-2" placeholder="Reason" value={ov.reason} onChange={(e) => setOv({ ...ov, reason: e.target.value })} data-testid="override-reason" />
      </div>
      <Btn variant="outline" size="sm" disabled={!ov.reason} loading={busy === "ov"} onClick={() => act("ov", "override", ov, "Exception recorded")} data-testid="override-btn">Record exception</Btn>
      {job.overrides.map((o) => <div key={o.id} className="text-xs text-amber-400">• {o.phase}: {o.reason} — {o.by}, {fmtDT(o.at)}</div>)}
    </Section>
  );
}

function Payments({ job, reload }) {
  const [p, setP] = useState(job.payments);
  const [busy, setBusy] = useState(false);
  const [hold, setHold] = useState(job.dispute_hold);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/jobs/${job.id}/payments`, p);
      toast.success("Payment records saved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const Part = ({ k, label }) => (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        <Select value={p[k].status || "unpaid"} onChange={(e) => setP({ ...p, [k]: { ...p[k], status: e.target.value } })} data-testid={`payment-${k}-status`}>
          <option value="unpaid">Unpaid</option><option value="partial">Partial</option><option value="paid">Paid</option><option value="refunded">Refunded</option>
        </Select>
        <Input type="number" step="0.01" placeholder="Amount" defaultValue={p[k].amount || ""} onBlur={(e) => setP({ ...p, [k]: { ...p[k], amount: e.target.value } })} data-testid={`payment-${k}-amount`} />
        <Input placeholder="Method (cash, Zelle…)" defaultValue={p[k].method || ""} onBlur={(e) => setP({ ...p, [k]: { ...p[k], method: e.target.value } })} data-testid={`payment-${k}-method`} />
        <Input type="date" defaultValue={p[k].date || ""} onBlur={(e) => setP({ ...p, [k]: { ...p[k], date: e.target.value } })} data-testid={`payment-${k}-date`} />
      </div>
    </div>
  );
  return (
    <Section title="Payments (manual records)" icon={DollarSign} testid="payments-section">
      <Part k="customer" label={`Customer payment · total ${usd(job.pricing.total)}`} />
      <Part k="payout" label={`Specialist payout · ${usd(job.pricing.payout)}`} />
      <Btn variant="outline" className="w-full" onClick={save} loading={busy} data-testid="payments-save-btn">Save payment records</Btn>
      <label className="flex gap-2 text-sm items-center pt-2 border-t border-[#1f1f1f]">
        <Flag className="w-4 h-4 text-red-400" />
        <input type="checkbox" checked={hold} onChange={async (e) => {
          setHold(e.target.checked);
          await api.post(`/admin/jobs/${job.id}/dispute`, { hold: e.target.checked, reason: "" });
          reload();
        }} className="accent-[#FF6B00]" data-testid="dispute-hold-toggle" />
        Dispute hold — preserve all records
      </label>
    </Section>
  );
}

export default function AdminJob() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [view, setView] = useState(null);
  const [missing, setMissing] = useState("");
  const [jump, setJump] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 403) setMissing(errMsg(e));
    }
  }, [id]);
  useEffect(() => {
    if (missing) return undefined;
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load, missing]);
  if (missing)
    return (
      <div className="text-center py-16 space-y-3" data-testid="job-not-found">
        <div className="text-neutral-300 font-medium">This job no longer exists</div>
        <div className="text-sm text-neutral-500">{missing}</div>
        <Link to="/a/jobs" className="text-[#FF6B00] text-sm">Back to Job board</Link>
      </div>
    );
  if (!job) return <div className="h-40 animate-pulse bg-[#141414] rounded-2xl" />;
  const active = !["draft", "open", "cancelled"].includes(job.status);
  return (
    <div className="space-y-4 fade-in" data-testid="admin-job-page">
      <div className="flex items-center justify-between">
        <Link to="/a/jobs" className="text-sm text-neutral-500 flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Jobs</Link>
        <div className="flex gap-1">
          <Link to={`/a/jobs/${id}/edit`}><Btn variant="ghost" size="sm" data-testid="edit-job-btn"><Pencil className="w-4 h-4" />Edit</Btn></Link>
          <Link to={`/a/jobs/${id}/report`}><Btn variant="ghost" size="sm" data-testid="report-btn"><FileText className="w-4 h-4" />Report</Btn></Link>
          <Btn variant="ghost" size="sm" onClick={() => downloadBlob(`/admin/jobs/${id}/photos.zip`, `job-${job.number}-photos.zip`).catch((e) => toast.error(errMsg(e)))} data-testid="download-photos-btn"><Download className="w-4 h-4" />Photos</Btn>
        </div>
      </div>
      {active && <PhotoJumpButton job={job} onJump={setJump} />}
      <JobSummary job={job} />
      {job.assigned_name && <div className="text-sm text-neutral-400" data-testid="assigned-specialist">Assigned: <span className="text-white">{job.assigned_name}</span> · {fmtDT(job.assigned_at)}</div>}
      <CustomerCard job={job} />
      {job.admin_notes && <Section title="Private notes"><p className="text-sm text-neutral-300 whitespace-pre-wrap">{job.admin_notes}</p></Section>}
      <Dispatch job={job} reload={load} />
      <Quotes job={job} reload={load} />
      <Section title="Intake photos" icon={Image}>
        <PhotoSlot jobId={job.id} phase="intake" slot="intake" label="Customer-sent photos" photos={job.photos} onUploaded={load} onView={setView} />
      </Section>
      {active && <NextStep job={job} reload={load} />}
      {active && <Condition job={job} user={user} reload={load} jump={jump} />}
      {active && <Review job={job} reload={load} />}
      <Payments key={JSON.stringify(job.payments)} job={job} reload={load} />
      <Section title="Timeline" icon={History} testid="timeline-section">
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {[...job.timeline].reverse().map((t, i) => (
            <div key={i} className="text-xs border-l-2 border-[#262626] pl-3">
              <div className="text-neutral-200">{t.action} <span className="text-neutral-500">· {t.actor}</span></div>
              {t.detail && <div className="text-neutral-500">{t.detail}</div>}
              <div className="font-mono text-neutral-600">{fmtDT(t.at)}</div>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Location" icon={ListChecks}>
        <Row k="Geocoded" v={job.geo ? job.geo.label : "Not located"} />
        <Row k="Driven remaining" v={<Money v={job.pricing.driven_remaining} green />} />
      </Section>
      <PhotoViewer photo={view} onClose={() => setView(null)} />
    </div>
  );
}
